/**
 * VintageSlider wrapper for the MARS Live panel.
 *
 * Delegates all rendering to the shared `@/components/VintageSlider` and adds
 * mars-live-specific prefetching: when the slider signals a prefetch, we
 * pre-warm the +-PREFETCH_RADIUS neighbors (plus their prev-month overlays)
 * in the TanStack Query cache via `prefetchQuery`.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchVintageMulti, findPrevMonth, mlKeys } from '../api';
import SharedVintageSlider from '@/components/VintageSlider';

const PREFETCH_RADIUS = 2;

export interface VintageSliderProps {
  vintages: string[];
  value: string;
  onChange: (vintage: string) => void;
  /** See the shared slider: pass false on duplicate instances. */
  keyboardNav?: boolean;
}

export default function VintageSlider({
  vintages,
  value,
  onChange,
  keyboardNav,
}: VintageSliderProps): JSX.Element {
  const queryClient = useQueryClient();

  /**
   * Prefetch the target vintage and its +-PREFETCH_RADIUS neighbors (plus the
   * prev-month overlay vintage) into the TanStack Query cache.
   */
  const handlePrefetch = useCallback(
    (vintage: string) => {
      if (!vintages.length) return;
      const targetIdx = vintages.indexOf(vintage);
      if (targetIdx < 0) return;

      const prefetchOne = (date: string): void => {
        const key = mlKeys.vintageMulti(date);
        queryClient
          .prefetchQuery({
            queryKey: key,
            queryFn: () => fetchVintageMulti(date),
            staleTime: 60_000,
          })
          .catch(() => {
            /* swallow -- prefetch is best-effort */
          });
      };

      for (let offset = -PREFETCH_RADIUS; offset <= PREFETCH_RADIUS; offset += 1) {
        const idx = targetIdx + offset;
        if (idx < 0 || idx >= vintages.length) continue;
        const date = vintages[idx];
        prefetchOne(date);
        const prev = findPrevMonth(vintages, date);
        if (prev) prefetchOne(prev);
      }
    },
    [queryClient, vintages],
  );

  return (
    <SharedVintageSlider
      vintages={vintages}
      value={value}
      onChange={onChange}
      onPrefetch={handlePrefetch}
      keyboardNav={keyboardNav}
    />
  );
}
